/**
 * Centralized text formatting and layout utilities for resume components
 * Consolidates bullet point rendering, spacing, margins, and layout logic
 * Used across ModalBasedResume, ResumePreview, Templates, and PDF generation
 */
import React from "react";

// ===============================
// SPACING AND MARGIN CONSTANTS
// ===============================

/**
 * Standard spacing values used throughout resume components
 */
export const SPACING = {
  // Section spacing
  SECTION_MARGIN_BOTTOM: "16px",
  SECTION_HEADER_MARGIN_BOTTOM: "8px",
  SECTION_HEADER_PADDING_BOTTOM: "3px",

  // Item spacing
  ITEM_MARGIN_BOTTOM: "14px",
  ITEM_MARGIN_TOP: "8px",

  // Field spacing
  FIELD_GAP: "8px",
  FIELD_GAP_SMALL: "6px",
  FIELD_GAP_LARGE: "12px",

  // Content spacing
  CONTENT_MARGIN_RIGHT: "8px",
  CONTENT_PADDING: "12px",

  // Button spacing
  BUTTON_HEIGHT: "26px",
  BUTTON_HEIGHT_LARGE: "28px",
  BUTTON_WIDTH: "26px",
  BUTTON_WIDTH_LARGE: "28px",

  // Grid and layout
  GRID_GAP: "2px 8px",
  GRID_COLUMN_GAP: "8px",

  // Bullet point spacing
  BULLET_MARGIN_BOTTOM: "3px",
  BULLET_MARGIN_RIGHT: "6px",
  BULLET_PADDING_LEFT: "15px",

  // Container spacing
  CONTAINER_PADDING: "20px",
  MODAL_MARGIN_BOTTOM: "12px",
};

/**
 * Font sizes used throughout resume components
 */
export const FONT_SIZES = {
  TITLE: "15px",
  SUBTITLE: "13px",
  BODY: "12px",
  SMALL: "10px",
  TINY: "9px",
};

/**
 * Colors used throughout resume components
 */
export const COLORS = {
  PRIMARY: "#333",
  SECONDARY: "#555",
  MUTED: "#666",
  LIGHT: "#999",
  BORDER: "#ccc",
  BACKGROUND: "#f5f5f5",
};

// ===============================
// BULLET POINT PATTERNS
// ===============================

/**
 * Patterns used to detect and clean bullet points
 */
export const BULLET_PATTERNS = {
  // Standard bullet symbols
  BULLETS: [/^[•·∙◦‣⁃▪▸►]\s*/, /^[-*+]\s*/, /^\d+[.)]\s*/],

  // All bullet patterns combined
  ALL_BULLETS: /^[•·∙◦‣⁃▪▸►\-*+]\s*|^\d+[.)]\s*/,

  // Numbered list patterns
  NUMBERED: /^\d+[.)]\s*/,

  // Symbol patterns
  SYMBOLS: /^[•·∙◦‣⁃▪▸►\-*+]\s*/,
};

// ===============================
// STYLE GENERATORS
// ===============================

/**
 * Generate standard section header styles
 * @param {Object} options - Style options
 * @returns {Object} - CSS styles object
 */
export const getSectionHeaderStyles = (options = {}) => ({
  margin: 0,
  fontSize: options.fontSize || FONT_SIZES.TITLE,
  fontWeight: "bold",
  color: options.color || COLORS.PRIMARY,
  marginBottom: options.marginBottom || SPACING.SECTION_HEADER_MARGIN_BOTTOM,
  borderBottom: options.borderBottom || `1px solid ${COLORS.BORDER}`,
  paddingBottom: options.paddingBottom || SPACING.SECTION_HEADER_PADDING_BOTTOM,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  ...options.additionalStyles,
});

/**
 * Generate standard section container styles
 * @param {Object} options - Style options
 * @returns {Object} - CSS styles object
 */
export const getSectionContainerStyles = (options = {}) => ({
  marginBottom: options.marginBottom || SPACING.SECTION_MARGIN_BOTTOM,
  position: "relative",
  ...options.additionalStyles,
});

/**
 * Generate standard item container styles
 * @param {Object} options - Style options
 * @returns {Object} - CSS styles object
 */
export const getItemContainerStyles = (options = {}) => ({
  marginBottom: options.marginBottom || SPACING.ITEM_MARGIN_BOTTOM,
  cursor: options.cursor || "default",
  position: "relative",
  ...options.additionalStyles,
});

/**
 * Generate standard flex container styles
 * @param {Object} options - Style options
 * @returns {Object} - CSS styles object
 */
export const getFlexContainerStyles = (options = {}) => ({
  display: "flex",
  justifyContent: options.justifyContent || "space-between",
  alignItems: options.alignItems || "baseline",
  marginBottom: options.marginBottom || "3px",
  gap: options.gap || SPACING.FIELD_GAP,
  ...options.additionalStyles,
});

/**
 * Generate grid layout styles for skills and tags
 * @param {Object} options - Grid options
 * @returns {Object} - CSS styles object
 */
export const getGridStyles = (options = {}) => ({
  display: "grid",
  gridTemplateColumns: options.columns || "1fr 1fr 1fr",
  gap: options.gap || SPACING.GRID_GAP,
  columnGap: options.columnGap || SPACING.GRID_COLUMN_GAP,
  ...options.additionalStyles,
});

/**
 * Generate button group styles
 * @param {Object} options - Button options
 * @returns {Object} - CSS styles object
 */
export const getButtonGroupStyles = (options = {}) => ({
  display: "flex",
  gap: options.gap || SPACING.FIELD_GAP_SMALL,
  flexShrink: 0,
  alignSelf: options.alignSelf || "flex-end",
  ...options.additionalStyles,
});

/**
 * Generate standard button styles
 * @param {Object} options - Button options
 * @returns {Object} - CSS styles object
 */
export const getButtonStyles = (options = {}) => ({
  fontSize: options.fontSize || "14px",
  height: options.height || SPACING.BUTTON_HEIGHT,
  width: options.width || SPACING.BUTTON_WIDTH,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  border: options.border || "1px solid #d9d9d9",
  backgroundColor: options.backgroundColor || "#fff",
  boxShadow: options.boxShadow || "0 2px 4px rgba(0,0,0,0.1)",
  borderRadius: options.borderRadius || "6px",
  ...options.additionalStyles,
});

// ===============================
// TEXT FORMATTING FUNCTIONS
// ===============================

/**
 * Clean and normalize text content
 * @param {string} text - Text to clean
 * @returns {string} - Cleaned text
 */
export const cleanText = (text) => {
  if (!text || typeof text !== "string") return "";
  return text.trim().replace(/\s+/g, " ");
};

/**
 * Check if a line contains a bullet point
 * @param {string} line - Text line to check
 * @returns {boolean} - True if line has bullet point
 */
export const hasBulletPoint = (line) => {
  if (!line || typeof line !== "string") return false;
  const trimmedLine = line.trim();
  return BULLET_PATTERNS.ALL_BULLETS.test(trimmedLine);
};

/**
 * Remove bullet points from text
 * @param {string} text - Text with potential bullets
 * @returns {string} - Text without bullets
 */
export const removeBulletPoints = (text) => {
  if (!text || typeof text !== "string") return "";
  return text.trim().replace(BULLET_PATTERNS.ALL_BULLETS, "").trim();
};

/**
 * Convert plain text to bullet point list
 * @param {string} text - Plain text or bulleted text
 * @returns {Array} - Array of bullet point items
 */
export const convertToBulletPoints = (text) => {
  if (!text || typeof text !== "string") return [];

  // Split by new lines and filter empty lines
  const lines = text.split(/\n+/).filter((line) => line.trim());

  return lines
    .map((line) => {
      const trimmedLine = line.trim();
      // Remove existing bullet points to standardize
      const cleanedLine = removeBulletPoints(trimmedLine);
      return cleanedLine;
    })
    .filter((line) => line.length > 0);
};

// ===============================
// BULLET POINT RENDERING
// ===============================

/**
 * Generate bullet point data for descriptions (experience, projects, etc.)
 * @param {string} description - Description text
 * @param {Object} options - Rendering options
 * @returns {Object|null} - Bullet point data and styles
 */
export const getBulletPointData = (description, options = {}) => {
  if (!description) return null;

  const bulletPoints = convertToBulletPoints(description);
  if (bulletPoints.length === 0) return null;

  const styles = {
    container: {
      fontSize: options.fontSize || FONT_SIZES.BODY,
      color: options.color || COLORS.SECONDARY,
      lineHeight: options.lineHeight || "1.4",
      ...options.containerStyles,
    },
    bulletLine: {
      display: "flex",
      alignItems: "flex-start",
      marginBottom: options.bulletMargin || SPACING.BULLET_MARGIN_BOTTOM,
      paddingLeft: options.paddingLeft || "0px",
      ...options.bulletLineStyles,
    },
    bulletSymbol: {
      fontSize: options.bulletSize || FONT_SIZES.BODY,
      color: options.bulletColor || COLORS.SECONDARY,
      marginRight: options.bulletSpacing || SPACING.BULLET_MARGIN_RIGHT,
      lineHeight: options.lineHeight || "1.4",
      marginTop: "0.5px",
      flexShrink: 0,
      ...options.bulletSymbolStyles,
    },
    bulletText: {
      fontSize: options.fontSize || FONT_SIZES.BODY,
      color: options.color || COLORS.SECONDARY,
      lineHeight: options.lineHeight || "1.4",
      flex: 1,
      ...options.bulletTextStyles,
    },
  };

  return {
    points: bulletPoints,
    styles,
  };
};

/**
 * Simplified function to create React elements for bullet points
 * This is used by ModalBasedResume.jsx and similar components
 */
export const createBulletElements = (description, options = {}, React) => {
  const data = getBulletPointData(description, options);
  if (!data) return null;

  const { points, styles } = data;

  return React.createElement(
    "div",
    { style: styles.container },
    points.map((point, index) =>
      React.createElement("div", { key: index, style: styles.bulletLine }, [
        React.createElement("span", { key: "bullet", style: styles.bulletSymbol }, "•"),
        React.createElement("span", { key: "text", style: styles.bulletText }, point),
      ])
    )
  );
};

/**
 * Get bullet point data for achievements
 * @param {string} description - Achievement description
 * @param {Object} options - Rendering options
 * @returns {Object|null} - Achievement bullet data
 */
export const getAchievementBulletData = (description, options = {}) => {
  const achievementOptions = {
    fontSize: FONT_SIZES.BODY,
    color: COLORS.SECONDARY,
    bulletColor: "#1890ff", // Blue bullets for achievements
    containerStyles: {
      backgroundColor: options.highlight ? "rgba(24, 144, 255, 0.02)" : "transparent",
      padding: options.highlight ? "8px" : "0",
      borderRadius: options.highlight ? "4px" : "0",
      border: options.highlight ? "1px solid rgba(24, 144, 255, 0.1)" : "none",
    },
    ...options,
  };

  return getBulletPointData(description, achievementOptions);
};

/**
 * Get bullet point data for projects
 * @param {string} description - Project description
 * @param {Object} options - Rendering options
 * @returns {Object|null} - Project bullet data
 */
export const getProjectBulletData = (description, options = {}) => {
  const projectOptions = {
    fontSize: FONT_SIZES.BODY,
    color: COLORS.SECONDARY,
    bulletColor: "#52c41a", // Green bullets for projects
    ...options,
  };

  return getBulletPointData(description, projectOptions);
};

/**
 * Get skills data and styles for rendering
 * @param {Array|Object} skills - Skills data
 * @param {Object} options - Rendering options
 * @returns {Object|null} - Skills data and styles
 */
export const getSkillsData = (skills, options = {}) => {
  if (!skills) return null;

  let skillsList = [];

  // Handle different skills data formats
  if (Array.isArray(skills)) {
    skillsList = skills;
  } else if (skills.list && Array.isArray(skills.list)) {
    skillsList = skills.list;
  } else if (skills.categorized) {
    // Handle categorized skills
    const categories = ["technical", "soft", "tools", "other"];
    skillsList = categories.reduce((acc, category) => {
      if (skills.categorized[category]) {
        acc.push(...skills.categorized[category]);
      }
      return acc;
    }, []);
  }

  if (skillsList.length === 0) return null;

  const isGrid = options.layout === "grid";
  const styles = {
    container: isGrid
      ? getGridStyles({
          columns: options.columns || "repeat(auto-fit, minmax(120px, 1fr))",
          gap: options.gap || "4px 8px",
        })
      : {
          display: "flex",
          flexWrap: "wrap",
          gap: options.gap || SPACING.FIELD_GAP,
        },
    skill: {
      fontSize: options.fontSize || FONT_SIZES.BODY,
      color: options.color || COLORS.SECONDARY,
      backgroundColor: options.backgroundColor || "#f0f0f0",
      padding: options.padding || "2px 8px",
      borderRadius: options.borderRadius || "4px",
      border: options.border || "1px solid #d9d9d9",
      ...options.skillStyles,
    },
  };

  return {
    skills: skillsList,
    styles,
  };
};

// ===============================
// LAYOUT UTILITIES
// ===============================

/**
 * Get styles for responsive content layout with actions
 * @param {Object} options - Layout options
 * @returns {Object} - Layout styles
 */
export const getContentLayoutStyles = (options = {}) => {
  return {
    container: {
      marginTop: options.marginTop || SPACING.ITEM_MARGIN_TOP,
      display: "flex",
      justifyContent: options.justifyContent || "space-between",
      alignItems: options.alignItems || "flex-end",
      ...options.containerStyles,
    },
    content: {
      flex: 1,
      marginRight: options.contentMargin || SPACING.CONTENT_MARGIN_RIGHT,
      ...options.contentStyles,
    },
    actions: {
      display: "flex",
      gap: options.actionGap || SPACING.FIELD_GAP_SMALL,
      flexShrink: 0,
      alignSelf: options.actionAlign || "flex-end",
      ...options.actionStyles,
    },
  };
};

/**
 * Get section styles for header and content
 * @param {Object} options - Section options
 * @returns {Object} - Section styles
 */
export const getSectionStyles = (options = {}) => {
  return {
    container: getSectionContainerStyles(options),
    header: getSectionHeaderStyles(options.headerOptions),
    titleSpan: {
      display: "flex",
      alignItems: "center",
      gap: SPACING.FIELD_GAP,
    },
  };
};

/**
 * Get empty state styles
 * @param {Object} options - Empty state options
 * @returns {Object} - Empty state styles
 */
export const getEmptyStateStyles = (options = {}) => {
  return {
    container: {
      textAlign: "center",
      padding: options.padding || SPACING.CONTAINER_PADDING,
      color: options.color || COLORS.LIGHT,
      ...options.containerStyles,
    },
  };
};

// ===============================
// PDF-SPECIFIC UTILITIES
// ===============================

/**
 * Convert React components to PDF-compatible format
 * @param {string} description - Description text
 * @param {Object} options - PDF options
 * @returns {Array} - PDF-compatible bullet points
 */
export const convertBulletsForPDF = (description, options = {}) => {
  if (!description) return [];

  const bulletPoints = convertToBulletPoints(description);

  return bulletPoints.map((point, index) => ({
    key: index,
    text: point,
    style: {
      fontSize: options.fontSize || 10,
      marginBottom: options.marginBottom || 3,
      paddingLeft: options.paddingLeft || 15,
    },
  }));
};

/**
 * Generate PDF-compatible styles
 * @param {Object} webStyles - Web CSS styles
 * @returns {Object} - PDF-compatible styles
 */
export const convertStylesForPDF = (webStyles) => {
  const pdfStyles = {};

  // Convert common web styles to PDF styles
  if (webStyles.fontSize) pdfStyles.fontSize = parseInt(webStyles.fontSize);
  if (webStyles.color) pdfStyles.color = webStyles.color;
  if (webStyles.marginBottom) pdfStyles.marginBottom = parseInt(webStyles.marginBottom);
  if (webStyles.marginTop) pdfStyles.marginTop = parseInt(webStyles.marginTop);
  if (webStyles.fontWeight) pdfStyles.fontWeight = webStyles.fontWeight;
  if (webStyles.fontStyle) pdfStyles.fontStyle = webStyles.fontStyle;

  return pdfStyles;
};

// ===============================
// VALIDATION UTILITIES
// ===============================

/**
 * Validate if content needs bullet formatting
 * @param {string} content - Content to validate
 * @returns {boolean} - True if content needs bullets
 */
export const needsBulletFormatting = (content) => {
  if (!content || typeof content !== "string") return false;

  const lines = content.split("\n").filter((line) => line.trim());
  return lines.length > 1 || (lines.length === 1 && lines[0].length > 100);
};

/**
 * Validate section completeness
 * @param {Object} sectionData - Section data
 * @param {Array} requiredFields - Required fields
 * @returns {Object} - Validation result
 */
export const validateSection = (sectionData, requiredFields = []) => {
  const missing = [];
  const warnings = [];

  requiredFields.forEach((field) => {
    if (
      !sectionData[field] ||
      (typeof sectionData[field] === "string" && !sectionData[field].trim()) ||
      (Array.isArray(sectionData[field]) && sectionData[field].length === 0)
    ) {
      missing.push(field);
    }
  });

  // Check for content quality
  if (sectionData.description && needsBulletFormatting(sectionData.description)) {
    const bullets = convertToBulletPoints(sectionData.description);
    if (bullets.length < 2) {
      warnings.push("Consider adding more bullet points for better impact");
    }
  }

  return {
    isValid: missing.length === 0,
    missing,
    warnings,
    score: Math.max(0, 100 - missing.length * 25 - warnings.length * 10),
  };
};

// ===============================
// EXPORTS
// ===============================

// Main formatting functions - these are now in TextFormattingComponents.jsx

// Default export for convenience
export default {
  // Constants
  SPACING,
  FONT_SIZES,
  COLORS,
  BULLET_PATTERNS,

  // Style generators
  getSectionHeaderStyles,
  getSectionContainerStyles,
  getItemContainerStyles,
  getFlexContainerStyles,
  getGridStyles,
  getButtonGroupStyles,
  getButtonStyles,

  // Text formatting
  cleanText,
  hasBulletPoint,
  removeBulletPoints,
  convertToBulletPoints,

  // Data generation
  getBulletPointData,
  getSkillsData,

  // Layout utilities
  getContentLayoutStyles,
  getSectionStyles,
  getEmptyStateStyles,

  // PDF utilities
  convertBulletsForPDF,
  convertStylesForPDF,

  // Validation
  needsBulletFormatting,
  validateSection,
};
